import { COST_SIMULATION_YEARS } from "@repo/shared";
import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./cost-chart-form.i18n";
import { CurrencyDisplayField } from "./currency-display-field";
import type { ComponentFormProps } from "./types";

export type CostChartConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  displayMode: "rolling" | "calendar";
  chartType?: "line" | "bar";
  projectionYears?: number;
};

type CostChartFormProps = ComponentFormProps<CostChartConfig>;

export function CostChartForm({ config, onChange }: CostChartFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="cost-chart-title">{t.title}</Label>
        <Input
          id="cost-chart-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-chart-description">{t.description}</Label>
        <Textarea
          id="cost-chart-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <CurrencyDisplayField
        blockType="cost_chart"
        value={config.currencyDisplay}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="space-y-2">
        <Label htmlFor="cost-chart-display-mode">{t.displayMode}</Label>
        <Select
          value={config.displayMode || "rolling"}
          onValueChange={(value: "rolling" | "calendar") =>
            onChange({ ...config, displayMode: value })
          }
        >
          <SelectTrigger id="cost-chart-display-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="rolling">{t.rollingPeriods}</SelectItem>
            <SelectItem value="calendar">{t.calendarYears}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.displayModeHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-chart-type">{t.chartType}</Label>
        <Select
          value={config.chartType || "line"}
          onValueChange={(value: "line" | "bar") => onChange({ ...config, chartType: value })}
        >
          <SelectTrigger id="cost-chart-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="line">{t.lineChart}</SelectItem>
            <SelectItem value="bar">{t.barChart}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{t.chartTypeHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="cost-chart-projection-years">{t.projectionYears}</Label>
        <Input
          id="cost-chart-projection-years"
          type="number"
          min={2}
          max={COST_SIMULATION_YEARS}
          value={config.projectionYears ?? 10}
          onChange={(e) => onChange({ ...config, projectionYears: Number(e.target.value) })}
        />
        <p className="text-xs text-muted-foreground">{t.projectionYearsHelp}</p>
      </div>

      <div className="p-3 bg-muted/50 rounded-md">
        <p className="text-xs text-muted-foreground">{t.componentDescription}</p>
      </div>
    </div>
  );
}

export function getDefaultCostChartConfig(): CostChartConfig {
  return {
    title: "Cost Projection",
    description: "Projected impact of costs on returns over time.",
    displayMode: "rolling",
    chartType: "line",
  };
}
